import React, { useContext, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import {
  OfflineModifiersContext,
  OfflineValuesContext,
  NetworkStatusValuesContext,
  BookValuesContext,
} from 'contexts';

import { makeStyles } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import { useToast, Button } from '@redshelf/redshelf-ui';

const useStyles = makeStyles((theme) => {
  return {
    toastTitle: {
      color: theme.palette.greyBlue[700],
      ...theme.typography.h6,
    },
    toastTitleContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    toastIcon: {
      margin: theme.spacing(0, 2.75, 0, 0),
      color: theme.palette.greyBlue[600],
    },
    toastMessage: {
      marginLeft: theme.spacing(5.75),
      wordBreak: 'break-all',
    },
    lineClamp: {
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
    },
    toastCancel: {
      marginLeft: 'auto',
      marginRight: theme.spacing(1.25),
    },
    // this fixes the toast flashing
    toastRoot: { transform: 'translate3d(0, 0, 0)' },
  };
});

const ToastDowloadingTitle = ({ classes }) => (
  <div className={classes.toastTitleContainer}>
    <GetApp className={classes.toastIcon} />
    <h4 className={classes.toastTitle}>Downloading in Progress</h4>
  </div>
);

const ToastDownloadingCancel = ({
  classes,
  setCancelDownloadDialogProps,
  marker = null,
  markersDownloadingRef,
}) => (
  <Button
    size="small"
    className={classes.toastCancel}
    variant="secondary"
    onClick={() => {
      setCancelDownloadDialogProps({
        isOpen: true,
        marker,
        markersDownloadingRef,
      });
    }}
  >
    Cancel
  </Button>
);

const DOWNLOADED_TYPE = {
  COMPLETE: 'COMPLETE',
  PARTIAL: 'PARTIAL',
};

export default function useDownloadToast({
  markersDownloaded,
  markersDownloading,
  markersProgress,
  markersToDownload,
  setCancelDownloadDialogProps,
  markersDownloadingRef,
}) {
  const classes = useStyles();
  const { enqueueToast, closeToast } = useToast();

  const { book } = useContext(BookValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const { downloadInterruptionError, openAccessOfflineContentDialog } =
    useContext(OfflineModifiersContext);
  const { wholeBookDownloading, wholeBookDownloaded } =
    useContext(OfflineValuesContext);

  // Toast for Downloading Message
  const enqueueDownloadingToast = useCallback(
    ({ key, label, marker }) =>
      enqueueToast(
        <div className={clsx(classes.toastMessage, classes.lineClamp)}>
          Downloading <span className="noTranslate">{label}</span>
        </div>,
        {
          action: (
            <ToastDownloadingCancel
              classes={classes}
              setCancelDownloadDialogProps={setCancelDownloadDialogProps}
              markersDownloadingRef={markersDownloadingRef}
              marker={marker}
            />
          ),
          className: classes.toastRoot,
          key: key,
          persist: true,
          preventDuplicate: true,
          title: <ToastDowloadingTitle classes={classes} />,
        },
      ),
    [
      classes,
      enqueueToast,
      setCancelDownloadDialogProps,
      markersDownloadingRef,
    ],
  );

  // Toast for Downloaded Message
  const enqueueDownloadedToast = useCallback(
    ({ label, type = DOWNLOADED_TYPE.COMPLETE }) => {
      const message =
        type === DOWNLOADED_TYPE.PARTIAL ? (
          <span>
            You’ve downloaded parts of{' '}
            <span className="noTranslate">{label}</span> for access offline.
          </span>
        ) : (
          <span>
            You’ve successfully downloaded{' '}
            <span className="noTranslate">{label}</span> for access offline.
          </span>
        );

      enqueueToast(<div className={classes.lineClamp}>{message}</div>, {
        variant: 'success',
        preventDuplicate: true,
      });
    },
    [classes, enqueueToast],
  );

  // downloadingMarkers keep track of markers which need a 'downloading' toast, also prevent duplicate toasts
  const downloadingMarkers = useRef([]);
  // markersWithOpenToast keep track of markers that has a 'downloading' toast open
  const markersWithOpenToast = useRef([]);
  // wholeBookDownloadingOpenToastRef keep track if a toast for wholeBook Downloading is open
  const wholeBookDownloadingOpenToastRef = useRef(false);

  const handleWholeBookDownloadingToast = useCallback(async () => {
    // If whole book is downloading, close all opened toast and open toast for 'completeBook' downloading
    if (!wholeBookDownloadingOpenToastRef.current) {
      markersWithOpenToast.current?.forEach(async (marker) => {
        await closeToast(marker.index + 1);
      });

      enqueueDownloadingToast({
        key: 'completeBook',
        label: book?.title,
      });
      wholeBookDownloadingOpenToastRef.current = true;
    }
  }, [book, closeToast, enqueueDownloadingToast]);

  const handleDownloadingToast = useCallback(async () => {
    // filter markers that has started downloading from markers that are set to download(markersToDownload)
    const markersWithProgress = markersToDownload?.size
      ? [...markersToDownload].filter(
          (m) =>
            markersProgress?.get(m)?.percentage > 0 &&
            !markersDownloaded.has(m),
        )
      : [];

    // for each markers that is downloading, open a 'downloading' toast,
    // add to downloadingMarkers and markersWithOpenToast lists
    markersWithProgress?.forEach((marker) => {
      if (!downloadingMarkers.current?.includes(marker)) {
        downloadingMarkers.current = [...downloadingMarkers.current, marker];
        markersWithOpenToast.current = [
          ...markersWithOpenToast.current,
          marker,
        ];
        enqueueDownloadingToast({
          key: marker.index + 1,
          label: marker?.label ?? '',
          marker,
        });
      }
    });

    // if there is no markersDownloading and no markers set to download, there is no marker that need 'downloading' toast
    if (!markersToDownload?.size && !markersDownloading?.size) {
      downloadingMarkers.current = [];
    }

    return () => {
      if (markersDownloading.size === 0) {
        downloadingMarkers.current = [];
      }
    };
  }, [
    enqueueDownloadingToast,
    markersProgress,
    markersDownloading,
    markersToDownload,
    markersDownloaded,
  ]);

  const handleWholeBookDownloadedToast = useCallback(async () => {
    // if 'completeBook' toast is not open, wholeBookDownloading has not started, so cannot have completed downloading wholeBook.
    if (!wholeBookDownloadingOpenToastRef.current) {
      return;
    }

    // close the 'completeBook` toast and display 'downloaded' toast when wholeBook has been downloaded.
    if (wholeBookDownloaded) {
      closeToast('completeBook');
      enqueueDownloadedToast({ label: book?.title });

      openAccessOfflineContentDialog();
      wholeBookDownloadingOpenToastRef.current = false;
    }
  }, [
    book,
    closeToast,
    enqueueDownloadedToast,
    openAccessOfflineContentDialog,
    wholeBookDownloaded,
  ]);

  const handleMarkersDownloadedToast = useCallback(async () => {
    let originalOpenToasts = markersWithOpenToast.current;

    const markersDownloadingWithParent = new Set(
      [[...markersDownloading]?.map((m) => m.withParents)].flat(),
    );

    // Otherwise, for all markers with open 'downloading' toasts check if:
    markersWithOpenToast.current?.forEach((marker) => {
      // marker is completely downloaded
      if (
        markersDownloaded.has(marker) &&
        !markersDownloadingWithParent.has(marker)
      ) {
        closeToast(marker?.index + 1);
        enqueueDownloadedToast({ label: marker?.label });

        markersWithOpenToast.current = markersWithOpenToast.current.filter(
          (m) => m.index !== marker.index,
        );
      }

      // marker download is cancelled (no more downloading or set to download)
      else if (
        !markersToDownload?.has(marker) &&
        !markersDownloadingWithParent.has(marker)
      ) {
        // if any child marker is in downloaded list, part of marker was/is downloaded
        if (marker.withDescendents.some((m) => markersDownloaded.has(m))) {
          enqueueDownloadedToast({
            label: marker?.label,
            type: DOWNLOADED_TYPE.PARTIAL,
          });
        }

        closeToast(marker?.index + 1);

        markersWithOpenToast.current = markersWithOpenToast.current.filter(
          (m) => m.index !== marker.index,
        );
        originalOpenToasts = markersWithOpenToast.current.filter(
          (m) => m.index !== marker.index,
        );
      }
    });

    if (!markersWithOpenToast.current.length && originalOpenToasts.length) {
      openAccessOfflineContentDialog();
    }
  }, [
    closeToast,
    enqueueDownloadedToast,
    markersDownloaded,
    markersDownloading,
    markersToDownload,
    openAccessOfflineContentDialog,
  ]);

  useEffect(
    function toastsForOfflineDownloading() {
      if (isOffline) {
        return;
      }

      if (wholeBookDownloading) {
        handleWholeBookDownloadingToast();
      } else {
        handleDownloadingToast();
      }
    },
    [
      handleDownloadingToast,
      handleWholeBookDownloadingToast,
      isOffline,
      wholeBookDownloading,
    ],
  );

  useEffect(
    function toastsForOfflineDownloaded() {
      if (wholeBookDownloaded) {
        handleWholeBookDownloadedToast();
      } else {
        handleMarkersDownloadedToast();
      }
    },
    [
      handleMarkersDownloadedToast,
      handleWholeBookDownloadedToast,
      wholeBookDownloaded,
    ],
  );

  useEffect(
    function handleWholeBookDownloadCancel() {
      if (
        !wholeBookDownloading &&
        !wholeBookDownloaded &&
        wholeBookDownloadingOpenToastRef.current
      ) {
        closeToast('completeBook');
        wholeBookDownloadingOpenToastRef.current = false;
      }
    },
    [closeToast, wholeBookDownloaded, wholeBookDownloading],
  );

  // If offline and error while downloading, reset 'downloading' toast list
  useEffect(
    function handleToastWhenDownloadInterrupted() {
      if (isOffline && downloadInterruptionError) {
        downloadingMarkers.current = [];
      }
    },
    [downloadInterruptionError, isOffline],
  );
}
